import {
  AfterViewInit,
  Component,
  ViewChild,
  output,
  input,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  MatPaginator,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import {
  MatTable,
  MatTableDataSource,
  MatTableModule,
} from '@angular/material/table';
import { MatCardModule } from '@angular/material/card';

export interface ReportTableItem {
  id: number;
  requestNumber: number;
  client: string;
  requester?: string;
  requestDate: string;
  areaIdentification: string;
  area: string;
  cities?: string[];
  city?: string;
  state?: string;
  cpfCnpj: string;
  car?: string;
  producer: string;
  areaStatus?: {
    code: string | null;
    description: string;
  };
  error?: string;
  contract: string;
  insurancePolicy: string;
  farmName: string;
}

export interface ReportTableColumn {
  key: keyof ReportTableItem;
  label: string;
  visible?: boolean;
  adminOnly?: boolean;
  sortable?: boolean;
  type?: 'text' | 'date' | 'status' | 'cities';
  /** Nome real da coluna no banco para ordenação */
  sortKey?: string;
}

export interface ReportTableAction {
  key: string;
  label: string;
  icon: string;
  action: (item: ReportTableItem) => void;
  condition?: (item: ReportTableItem) => boolean;
  adminOnly?: boolean;
}

export interface ReportTableConfig {
  showActionsColumn: boolean;
  columns: ReportTableColumn[];
  initialSort?: Sort;
  statusClassFunction?: (
    statusCode: string,
  ) => 'chip-compliant' | 'chip-non-compliant' | null;
  isAdmin?: boolean;
}

@Component({
  selector: 'app-reports-table',
  standalone: true,
  imports: [
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatIconModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
  ],
  templateUrl: './reports-table.component.html',
  styleUrl: './reports-table.component.scss',
  providers: [provideNativeDateAdapter()],
})
export class ReportsTableComponent implements AfterViewInit {
  dataSource = input(new MatTableDataSource<ReportTableItem>([]));
  isLoading = input(false);
  length = input(0);
  config = input.required<ReportTableConfig>();
  actions = input<ReportTableAction[]>([]);

  sortChange = output<Sort>();
  pageChange = output<PageEvent>();

  @ViewChild(MatSort) declare sort: MatSort;
  @ViewChild(MatPaginator) declare paginator: MatPaginator;
  @ViewChild(MatTable) declare table: MatTable<ReportTableItem>;

  get visibleColumns(): ReportTableColumn[] {
    return this.config().columns.filter((column) =>
      this.shouldShowColumn(column),
    );
  }

  get displayedColumns(): string[] {
    const columns: string[] = this.visibleColumns.map(
      (column: ReportTableColumn) => column.key,
    );

    if (this.config().showActionsColumn && this.visibleActions.length > 0) {
      columns.push('actions');
    } else {
      this.visibleActions.forEach((action) => columns.push(action.key));
    }

    return columns;
  }

  get visibleActions(): ReportTableAction[] {
    return this.actions().filter((action) => this.shouldShowAction(action));
  }

  formatDate(dateValue: unknown): string {
    if (!dateValue) return '';

    if (typeof dateValue === 'string') {
      const date = new Date(dateValue);
      return (
        date.toLocaleDateString('pt-BR') +
        ' ' +
        date.toLocaleTimeString('pt-BR')
      );
    }

    if (dateValue instanceof Date) {
      return (
        dateValue.toLocaleDateString('pt-BR') +
        ' ' +
        dateValue.toLocaleTimeString('pt-BR')
      );
    }

    return String(dateValue);
  }

  private shouldShowColumn(column: ReportTableColumn): boolean {
    if (column.visible === false) return false;

    const config = this.config();
    const isAdmin = config.isAdmin ?? false;

    // Check admin-only columns
    if (column.adminOnly && !isAdmin) return false;

    return true;
  }

  private shouldShowAction(action: ReportTableAction): boolean {
    const config = this.config();
    const isAdmin = config.isAdmin ?? false;

    // Check admin-only actions
    if (action.adminOnly && !isAdmin) return false;

    return true;
  }

  ngAfterViewInit() {
    if (this.sort) {
      const config = this.config();
      if (config.initialSort?.active) {
        this.sort.active = config.initialSort.active;
        this.sort.direction = config.initialSort?.direction ?? 'desc';
      }
    }

    console.log('this.dataSource', this.dataSource());
  }

  onSortChange(event: Sort) {
    // Map frontend column name to backend column name
    const mappedSort = this.mapSortToBackend(event);
    this.sortChange.emit(mappedSort);
  }

  private mapSortToBackend(sort: Sort): Sort {
    const column = this.config().columns.find((col) => col.key === sort.active);

    if (column?.sortKey) {
      // Use the mapped backend column name
      return {
        active: column.sortKey,
        direction: sort.direction,
      };
    }

    // Use the original column name if no mapping exists
    return sort;
  }

  onPageChange(event: PageEvent) {
    this.pageChange.emit(event);
  }

  getStatusClass(statusCode: string): string {
    return this.config().statusClassFunction?.(statusCode) ?? '';
  }

  executeAction(
    action: ReportTableAction,
    item: ReportTableItem,
    event: Event,
  ) {
    event.stopPropagation();
    action.action(item);
  }

  shouldShowActionForItem(
    action: ReportTableAction,
    item: ReportTableItem,
  ): boolean {
    return !action.condition || action.condition(item);
  }

  getColumnLabel(columnKey: string): string {
    const column = this.config().columns.find((col) => col.key === columnKey);
    return column?.label || columnKey;
  }

  isColumnSortable(columnKey: string): boolean {
    const column = this.config().columns.find((col) => col.key === columnKey);
    return column?.sortable ?? true;
  }

  formatCitiesText(cities: string[]): string {
    if (!cities || cities.length === 0) return '-';
    if (cities.length === 1) return cities[0];

    // Retorna "Nome do Primeiro + (X)"
    return `${cities[0]} (+${cities.length - 1})`;
  }

  formatCitiesTooltip(cities: string[]): string {
    if (!cities || cities.length <= 1) return '';

    // Retorna a lista completa separada por vírgula (ou quebra de linha se preferir)
    return cities.join(', ');
  }

  protected shouldShowActionsMenu(
    actions: ReportTableAction[],
    item: ReportTableItem,
  ) {
    return actions.some((action) => this.shouldShowActionForItem(action, item));
  }
}
